// ═══════════════════════════════════════════════════════════════
// InfoBubble — small hover/click tooltip for inline help
// Path: src/components/ui/InfoBubble.jsx
//
// Usage:
//   <InfoBubble text="Explains this thing in one or two sentences." />
//
// Appears as a subtle ? icon; hover (or tap on mobile) reveals
// the tooltip. Pure inline styles — no library, no portal.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { HelpCircle } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";

export const InfoBubble = ({ text, size = 13, inline = false, placement = "top" }) => {
  const { palette } = useApp();
  const [open, setOpen] = useState(false);

  if (!text) return null;

  const wrapperStyle = {
    position: "relative",
    display: inline ? "inline-flex" : "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
    marginLeft: 5,
    cursor: "help",
  };

  const tooltipPos = {
    top: { bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)" },
    bottom: { top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)" },
    right: { left: "calc(100% + 8px)", top: "50%", transform: "translateY(-50%)" },
  }[placement] || { bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)" };

  const tooltipStyle = {
    position: "absolute",
    ...tooltipPos,
    width: 260,
    maxWidth: "70vw",
    padding: "10px 12px",
    borderRadius: 8,
    background: "#1f2937",
    color: "#f9fafb",
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: FONT_FAMILY,
    fontWeight: 400,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    zIndex: 100,
    pointerEvents: "none",
    whiteSpace: "normal",
  };

  return (
    <span
      style={wrapperStyle}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => {
        e.stopPropagation();
        setOpen((v) => !v);
      }}
    >
      <HelpCircle
        size={size}
        color={palette?.text3 || "#94a3b8"}
        style={{ opacity: 0.7 }}
      />
      {open && <span style={tooltipStyle}>{text}</span>}
    </span>
  );
};
